import {
  CityChronicleCategory,
  CityChronicleEventType,
  CityChronicleItem,
  CityChronicleResponse,
  CityChronicleTier,
} from "../types/cityChronicle"

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value.trim() || fallback
  if (value == null) return fallback
  return String(value).trim() || fallback
}

/** ISO для occurredAt — бэкенд иногда отдаёт Date-like / number / уже строку. */
function pickIsoOccurredAt(value: unknown): string | null {
  if (value == null || value === "") return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString()
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) return null
    const date = new Date(trimmed)
    return Number.isNaN(date.getTime()) ? trimmed : date.toISOString()
  }
  return null
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function pickChronicleCursor(value: unknown, fallbackItemId?: string): string | null {
  if (value == null || value === "") return null
  const raw = pickString(value)
  if (!raw) return null
  if (UUID_RE.test(raw)) return raw
  // Бэкенд иногда отдаёт ISO вместо uuid — тогда пагинация по id последнего элемента
  if (fallbackItemId && UUID_RE.test(fallbackItemId)) return fallbackItemId
  return null
}

function pickNumberOrNull(value: unknown): number | null {
  if (value == null || value === "") return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const CHRONICLE_CATEGORIES = new Set<CityChronicleCategory>([
  "WAR",
  "CARDS",
  "HEROES",
  "TOURNAMENTS",
  "CITY",
  "MAP",
  "DIGEST",
])

const CHRONICLE_TIERS = new Set<CityChronicleTier>([
  "EPIC",
  "RARE",
  "COMMON",
  "DIGEST",
  "INFO",
])

const CHRONICLE_EVENT_TYPES = new Set<CityChronicleEventType>([
  "WAR_TIER_UP",
  "WAR_TIER_DOWN",
  "WAR_PARITY",
  "WAR_TOP_CONTRIBUTOR",
  "POINT_CAPTURED",
  "BOSS_DEFEATED",
  "WORLD_BOSS_ENGAGED",
  "WORLD_BOSS_DEFEATED",
  "TOURNAMENT_CITY_FINAL",
  "TOURNAMENT_LEAGUE_FINAL",
  "TOURNAMENT_REGISTRATION_OPEN",
  "TOURNAMENT_VIRTUAL_WIN",
  "CARD_FOUND_ON_SCAN",
  "CARD_BOUGHT_IN_SHOP",
  "CARD_RANK_UP",
  "CARD_FIRST_IN_CITY",
  "SPIRIT_AWAKENED",
  "GUARDIAN_RANK_UP",
  "PET_LEVEL_UP",
  "PHYSICAL_CARD_REDEEMED",
  "LEADERBOARD_LOCAL_FIRST",
  "MEMBER_JOINED",
  "REFERRAL_REWARDED",
  "LEAGUE_MILESTONE",
  "GUARDIAN_PATH_DIGEST",
  "MAP_PEAK_ONLINE",
  "DAILY_ACTIVITY_DIGEST",
  "DAILY_STATS_DIGEST",
  "INFRA_SCAN_POINT",
  "INFRA_BOSS_POINT",
  "INFRA_CARD_SHOP",
  "INFRA_POST_OFFICE",
  "INFRA_TREASURE_POINT",
  "TREASURE_CLAIMED",
])

function normalizeCategory(value: unknown): CityChronicleCategory {
  const raw = pickString(value).toUpperCase()
  return CHRONICLE_CATEGORIES.has(raw as CityChronicleCategory)
    ? (raw as CityChronicleCategory)
    : "CITY"
}

function normalizeTier(value: unknown): CityChronicleTier {
  const raw = pickString(value).toUpperCase()
  return CHRONICLE_TIERS.has(raw as CityChronicleTier)
    ? (raw as CityChronicleTier)
    : "COMMON"
}

function normalizeEventType(value: unknown): CityChronicleEventType {
  const raw = pickString(value).toUpperCase()
  return CHRONICLE_EVENT_TYPES.has(raw as CityChronicleEventType)
    ? (raw as CityChronicleEventType)
    : "MEMBER_JOINED"
}

function normalizePayload(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {}
  return value as Record<string, unknown>
}

function normalizeCityChronicleItem(raw: unknown): CityChronicleItem | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const id = pickString(obj.id)
  const title = pickString(obj.title)
  if (!id || !title) return null

  const bodyRaw = obj.body
  const body =
    bodyRaw == null || bodyRaw === ""
      ? null
      : pickString(bodyRaw) || null

  const occurredAt = pickIsoOccurredAt(obj.occurredAt ?? obj.occurred_at)
  if (!occurredAt) return null

  return {
    id,
    type: normalizeEventType(obj.type),
    tier: normalizeTier(obj.tier),
    category: normalizeCategory(obj.category),
    title,
    body,
    payload: normalizePayload(obj.payload),
    actorUserId: pickNumberOrNull(obj.actorUserId ?? obj.actor_user_id),
    occurredAt,
  }
}

export function normalizeCityChronicleResponse(data: unknown): CityChronicleResponse {
  const root =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const nested = root.data ?? root.result ?? root.payload
  const source =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : root

  const itemsRaw = Array.isArray(source.items) ? source.items : []
  const items = itemsRaw
    .map(normalizeCityChronicleItem)
    .filter((item): item is CityChronicleItem => item != null)

  const lastId = items.length > 0 ? items[items.length - 1].id : undefined
  const nextCursorRaw = source.nextCursor ?? source.next_cursor
  const nextCursor = pickChronicleCursor(nextCursorRaw, lastId)

  return { items, nextCursor }
}
